/**
 * Direct AppSync GraphQL for TherapistPilotInvite / TherapistPilotRedemption.
 * Use when amplify_outputs model_introspection is stale (generateClient has no .models.TherapistPilotInvite).
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import outputs from '../../amplify_outputs.json';

const graphqlUrl = outputs.data?.url as string | undefined;
const apiKey = outputs.data?.api_key as string | undefined;

type GraphqlError = { message: string };

export type PilotInviteRecord = {
  id: string;
  status?: string | null;
  codeNormalized: string;
  trialDurationDays?: number | null;
  maxRedemptions?: number | null;
  redemptionCount?: number | null;
  inviteExpiresAt?: string | null;
};

export type PilotRedemptionRecord = {
  id: string;
  inviteId: string;
  userId: string;
  redeemedAt?: string | null;
};

export async function runGraphql<T>(
  query: string,
  variables: Record<string, unknown> | undefined,
  auth: 'apiKey' | 'userPool',
): Promise<T> {
  if (!graphqlUrl) {
    throw new Error('Missing data.url in amplify_outputs.json');
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (auth === 'apiKey') {
    if (!apiKey) throw new Error('Missing data.api_key in amplify_outputs.json');
    headers['x-api-key'] = apiKey;
  } else {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      throw new Error('You need to be signed in to use an invite code.');
    }
    headers.Authorization = token;
  }

  const res = await fetch(graphqlUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  });
  const json = (await res.json()) as { data?: T; errors?: GraphqlError[] };
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join('; '));
  }
  if (!json.data) {
    throw new Error('GraphQL response missing data');
  }
  return json.data;
}

const LIST_INVITES_BY_CODE = /* GraphQL */ `
  query ListTherapistPilotInvites($filter: ModelTherapistPilotInviteFilterInput, $limit: Int) {
    listTherapistPilotInvites(filter: $filter, limit: $limit) {
      items {
        id
        status
        codeNormalized
        trialDurationDays
        maxRedemptions
        redemptionCount
        inviteExpiresAt
      }
    }
  }
`;

const LIST_REDEMPTIONS = /* GraphQL */ `
  query ListTherapistPilotRedemptions($filter: ModelTherapistPilotRedemptionFilterInput, $limit: Int) {
    listTherapistPilotRedemptions(filter: $filter, limit: $limit) {
      items {
        id
        inviteId
        userId
        redeemedAt
      }
    }
  }
`;

const CREATE_REDEMPTION = /* GraphQL */ `
  mutation CreateTherapistPilotRedemption($input: CreateTherapistPilotRedemptionInput!) {
    createTherapistPilotRedemption(input: $input) {
      id
    }
  }
`;

const UPDATE_INVITE = /* GraphQL */ `
  mutation UpdateTherapistPilotInvite($input: UpdateTherapistPilotInviteInput!) {
    updateTherapistPilotInvite(input: $input) {
      id
    }
  }
`;

const LIST_INVITES_PAGE = /* GraphQL */ `
  query ListTherapistPilotInvites($limit: Int, $nextToken: String) {
    listTherapistPilotInvites(limit: $limit, nextToken: $nextToken) {
      items {
        id
        codeNormalized
        status
        trialDurationDays
        maxRedemptions
        redemptionCount
        inviteExpiresAt
        notes
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const LIST_REDEMPTIONS_PAGE = /* GraphQL */ `
  query ListTherapistPilotRedemptions($limit: Int, $nextToken: String) {
    listTherapistPilotRedemptions(limit: $limit, nextToken: $nextToken) {
      items {
        id
        inviteId
        userId
        redeemedAt
      }
      nextToken
    }
  }
`;

export async function findPilotInviteByCode(
  codeNormalized: string,
  auth: 'apiKey' | 'userPool' = 'userPool',
): Promise<PilotInviteRecord | null> {
  const data = await runGraphql<{
    listTherapistPilotInvites: { items: Array<PilotInviteRecord | null> | null };
  }>(
    LIST_INVITES_BY_CODE,
    { filter: { codeNormalized: { eq: codeNormalized } }, limit: 25 },
    auth,
  );
  const rows = (data.listTherapistPilotInvites.items || []).filter(Boolean) as PilotInviteRecord[];
  return rows.find((r) => String(r.status || '').toUpperCase() === 'ACTIVE') ?? null;
}

export async function listPilotRedemptionsForInvite(
  inviteId: string,
  auth: 'apiKey' | 'userPool' = 'userPool',
): Promise<PilotRedemptionRecord[]> {
  const data = await runGraphql<{
    listTherapistPilotRedemptions: { items: Array<PilotRedemptionRecord | null> | null };
  }>(LIST_REDEMPTIONS, { filter: { inviteId: { eq: inviteId } }, limit: 50 }, auth);
  return (data.listTherapistPilotRedemptions.items || []).filter(Boolean) as PilotRedemptionRecord[];
}

export async function createPilotRedemption(input: {
  inviteId: string;
  userId: string;
  redeemedAt: string;
}): Promise<void> {
  await runGraphql(CREATE_REDEMPTION, { input }, 'userPool');
}

export async function incrementPilotInviteRedemption(input: {
  id: string;
  redemptionCount: number;
  updatedAt: string;
}): Promise<void> {
  await runGraphql(UPDATE_INVITE, { input }, 'userPool');
}

/** Admin / scripts: paginated list with API key. */
export async function listAllPilotInvitesApiKey(): Promise<
  Array<{
    id: string;
    codeNormalized: string;
    status: string | null;
    trialDurationDays: number;
    maxRedemptions: number;
    redemptionCount: number;
    inviteExpiresAt: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string | null;
  }>
> {
  const items: Array<{
    id: string;
    codeNormalized: string;
    status: string | null;
    trialDurationDays: number;
    maxRedemptions: number;
    redemptionCount: number;
    inviteExpiresAt: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string | null;
  }> = [];
  let nextToken: string | null | undefined;
  do {
    const data = await runGraphql<{
      listTherapistPilotInvites: {
        items: Array<{
          id: string;
          codeNormalized: string;
          status: string | null;
          trialDurationDays: number;
          maxRedemptions: number;
          redemptionCount: number;
          inviteExpiresAt: string | null;
          notes: string | null;
          createdAt: string;
          updatedAt: string | null;
        } | null>;
        nextToken?: string | null;
      };
    }>(LIST_INVITES_PAGE, { limit: 200, nextToken: nextToken ?? undefined }, 'apiKey');
    for (const row of data.listTherapistPilotInvites.items || []) {
      if (row?.id && row.codeNormalized) items.push(row);
    }
    nextToken = data.listTherapistPilotInvites.nextToken;
  } while (nextToken);
  return items;
}

export async function listAllPilotRedemptionsApiKey(): Promise<PilotRedemptionRecord[]> {
  const items: PilotRedemptionRecord[] = [];
  let nextToken: string | null | undefined;
  do {
    const data = await runGraphql<{
      listTherapistPilotRedemptions: {
        items: Array<PilotRedemptionRecord | null>;
        nextToken?: string | null;
      };
    }>(LIST_REDEMPTIONS_PAGE, { limit: 200, nextToken: nextToken ?? undefined }, 'apiKey');
    for (const row of data.listTherapistPilotRedemptions.items || []) {
      if (row?.id) items.push(row);
    }
    nextToken = data.listTherapistPilotRedemptions.nextToken;
  } while (nextToken);
  return items;
}
